import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global "X min read" badge, styled after the bioland case-study meta line.
 * It measures the visible text of the current page (excluding the shared
 * top navigation and footer) and estimates reading time, so every route
 * shows its own correct minute count without hardcoding per page.
 */
const WORDS_PER_MINUTE = 260;

// Only running prose counts toward reading time. Headings, buttons, eyebrows,
// captions and link labels are scanned, not read.
const PROSE_SELECTOR = 'p, li, blockquote';

const countWords = (el: HTMLElement | null): number => {
  if (!el) return 0;
  const text = el.innerText || '';
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const ReadTime: React.FC = () => {
  const location = useLocation();
  const [minutes, setMinutes] = useState(0);

  useEffect(() => {
    // Wait a tick so the page's content has rendered before measuring.
    const timer = window.setTimeout(() => {
      const root = document.querySelector('.App') as HTMLElement | null;
      if (!root) {
        setMinutes(0);
        return;
      }
      let words = 0;
      root.querySelectorAll(PROSE_SELECTOR).forEach((el) => {
        // Skip the shared chrome, and any block nested inside another prose
        // block, so its words are not counted twice.
        if (el.closest('nav, footer')) return;
        if (el.parentElement?.closest(PROSE_SELECTOR)) return;
        words += countWords(el as HTMLElement);
      });
      setMinutes(words > 0 ? Math.max(1, Math.round(words / WORDS_PER_MINUTE)) : 0);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  if (!minutes) return null;

  return (
    <div className="global-read-time" aria-label={`${minutes} minute read`}>
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" className="grt-icon">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{minutes} min read</span>
    </div>
  );
};

export default ReadTime;
